import { Store, select } from '@ngrx/store';
import { Platform } from '@ionic/angular';
import { Inject, Injectable } from '@angular/core';
import { debounceTime, distinctUntilChanged, filter, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { BehaviorSubject, Observable, Subscription, combineLatest } from 'rxjs';

import { markEntityAsDirty } from '../../data/actions';
import { NativeStorageService } from '../../data/native-storage.service';
import { LoginState } from '../models';

import { defaultLoginState, initialSettingsState } from './reducer';
import { selectLanguage, updateSettingsState, updateSettingsValue, updateUserMetadataValue } from './actions';
import { LanguageSelector, LoginSelector, LoginCheckedSelector, SettingsLoadedSelector } from './selectors';
import { DEFAULT_SETTINGS_VALUE } from './default-settings-value';
import { toSignal } from '@angular/core/rxjs-interop';

export const ADMIN_SHELL_ROLES = ['app_admin', 'app_analytics'];

@Injectable()
export class SettingsService {
    private hideAdminForSession$ = new BehaviorSubject(false);

    settingsLoaded$ = this.store.select(SettingsLoadedSelector);
    settingsLoaded = toSignal(this.settingsLoaded$);

    loginChecked$ = this.store.pipe(select(LoginCheckedSelector));
    login$: Observable<LoginState> = this.loginChecked$.pipe(
        distinctUntilChanged(),
        tap((x) => console.log({ loginChecked: x })),
        switchMap((isChecked) =>
            this.store.pipe(
                select(LoginSelector),
                map((l) => {
                    console.log({ l });
                    if (isChecked) {
                        return l;
                    }
                    return { ...defaultLoginState, error: l.error, loading: l.loading };
                })
            )
        ),
        shareReplay(1)
    );
    login = toSignal(this.login$);

    loggedIn$: Observable<boolean> = this.login$.pipe(
        map((l) => !!l.user),
        distinctUntilChanged()
    );
    loggedIn = toSignal(this.loggedIn$);

    isAdmin$ = combineLatest([this.login$.pipe(map((l) => !!l.user?.roles?.includes('app_admin'))), this.hideAdminForSession$]).pipe(
        map(([isAdmin, isHidden]) => isAdmin && !isHidden)
    );
    isAdmin = toSignal(this.isAdmin$);

    isStaff$ = combineLatest([
        this.login$.pipe(map((l) => ADMIN_SHELL_ROLES.some((r) => !!l.user?.roles?.includes(r)))),
        this.hideAdminForSession$
    ]).pipe(map(([isStaff, isHidden]) => isStaff && !isHidden));
    isStaff = toSignal(this.isStaff$);

    selectedLanguage$ = this.store.pipe(select(LanguageSelector));
    settings$ = this.loggedIn$.pipe(
        filter((l) => l),
        switchMap(() => this.store.select((s) => s.settings)),
        distinctUntilChanged()
    );
    settings = toSignal(this.settings$);

    // Debounced version of settings for use cases that need to wait for settings to stabilize
    settingsDebounced$ = this.settings$.pipe(
        debounceTime(500), // Wait 500ms for settings to stabilize
        distinctUntilChanged()
    );
    settingsDebounced = toSignal(this.settingsDebounced$);

    settingsSubscription: Subscription;

    constructor(
        private store: Store<any>,
        private storage: NativeStorageService,
        platform: Platform,
        @Inject(DEFAULT_SETTINGS_VALUE) private defaultSettingsValue = {}
    ) {
        platform.ready().then((_) => {
            this.load();
        });
    }

    mergeObjects(defaultObj, overrides) {
        if (!defaultObj) {
            return overrides;
        }
        const res = structuredClone(defaultObj);

        const keys = Object.keys(overrides);
        for (const key of keys) {
            if (overrides.hasOwnProperty(key) && typeof overrides[key] === 'object' && !Array.isArray(overrides[key])) {
                res[key] = this.mergeObjects(defaultObj[key], overrides[key]);
            } else {
                res[key] = overrides[key];
            }
        }
        return res;
    }

    load() {
        console.log({ initialSettingsState });
        this.storage.getItem('settings', initialSettingsState).then((value) => {
            const settingsStatus = this.mergeObjects(this.defaultSettingsValue, value);
            console.log({ settingsStatus, defaultSettingsValue: this.defaultSettingsValue, value });
            this.store.dispatch(updateSettingsState(settingsStatus));
            if (!this.settingsSubscription) {
                this.settingsSubscription = this.settings$.pipe(debounceTime(1000)).subscribe((s) => {
                    const newSettings = JSON.parse(window.localStorage.getItem('CapacitorStorage.settings'));

                    if (JSON.stringify(newSettings) === JSON.stringify(s)) {
                        console.log('Settings values are equal.  Skipping localStorage update.');
                        return;
                    }

                    this.storage.setItem('settings', {
                        ...settingsStatus,
                        ...s
                    });
                });
            }
        });
    }

    updateValue(gameId: string, key: string, value: any) {
        this.dispatchNewValue(gameId, key, value);
        this.markAsDirty();
    }

    updateUserMetadataValue(key: string, value: any) {
        // This is effectively a readonly update.
        // Metadata is only updated server side, but only passed to the app on login
        // So this allows us to update the app metadata without logging in again
        // e.g. the voucher code
        this.store.dispatch(updateUserMetadataValue({ key, value }));
    }

    dispatchNewValue(gameId: string, key: string, value: any) {
        this.store.dispatch(updateSettingsValue({ gameId, settingsKey: key, newValue: value }));
    }

    selectLanguage(language: string) {
        this.store.dispatch(selectLanguage({ language }));
        this.markAsDirty();
    }

    hideAdminForSession() {
        this.hideAdminForSession$.next(true);
    }

    resetAdminForSession() {
        this.hideAdminForSession$.next(false);
    }

    private markAsDirty() {
        this.store.dispatch(markEntityAsDirty({ entityType: 'settings' }));
    }
}
